import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { convertMoneyToMinorUnits } from '@theme/money-formatting';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

// Keep the visual confirmation long enough to read without delaying the cart drawer.
const ADD_TO_CART_BUTTON_ADDED_DURATION = 1200;
const PRODUCT_ADD_TO_CART_RESET_DELAY = 0;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  /** @type {number} */
  #animationRunId = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#clearResetTimeouts();
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  #clearResetTimeouts() {
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
      return;
    }

    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;
    const animationRunId = ++this.#animationRunId;

    this.#clearResetTimeouts();

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);
    if (animationRunId !== this.#animationRunId) return;

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, ADD_TO_CART_BUTTON_ADDED_DURATION);

    this.#resetTimeouts.push(timeoutId);
  };

  resetAddToCartAnimation = function (delay = 0) {
    const { addToCartButton } = this.refs;
    this.#animationRunId += 1;
    this.#clearResetTimeouts();

    const reset = () => {
      addToCartButton.removeAttribute('data-added');
    };

    if (delay <= 0) {
      reset();
      return;
    }

    const timeoutId = setTimeout(() => {
      reset();
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, delay);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number, finish?: (error?: Error) => void}>} */
  #addToCartQueue = [];

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    for (const item of this.#addToCartQueue) {
      item.finish?.(new Error('The product changed before it could be added. Please review your cart.'));
    }
    this.#addToCartQueue = [];
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    this.querySelectorAll('[data-hp-addon-variant-select] option').forEach((option) => {
      if (!(option instanceof HTMLOptionElement)) return;
      const optionCartQuantity = cart.items.reduce((total, item) => {
        if (item.variant_id.toString() !== option.value.toString()) return total;
        return total + (Number(item.quantity) || 0);
      }, 0);
      option.dataset.cartQuantity = String(optionCartQuantity);
    });

    const cartQty = cart.items.reduce((total, item) => {
      if (item.variant_id.toString() !== variantIdInput.value.toString()) return total;
      return total + (Number(item.quantity) || 0);
    }, 0);
    this.dataset.cartQuantity = String(cartQty);

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);
    this.syncStandaloneAvailability();

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  refreshCartQuantity() {
    return this.#fetchAndUpdateCartQuantity();
  }

  syncStandaloneAvailability() {
    if (!this.closest('[data-hp-product-addon-item]') || this.refs.quantitySelector) return;

    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-hp-addon-submit]'));
    const label = button?.querySelector('[data-hp-button-label]');
    if (!button) return;

    const maxQuantity = this.#getVariantMaxQuantity();
    const cartQuantity = Math.max(0, Number(this.dataset.cartQuantity) || 0);
    const isAvailable = this.dataset.variantAvailable !== 'false';
    const isAtLimit = maxQuantity !== null && cartQuantity >= maxQuantity;
    const defaultLabel = button.dataset.hpDefaultLabel || 'Add to cart';

    button.disabled = !isAvailable || isAtLimit;
    button.toggleAttribute('data-hp-inventory-limit', isAtLimit);

    if (label instanceof HTMLElement) {
      label.textContent = !isAvailable
        ? 'Sold out'
        : isAtLimit
          ? 'Maximum in cart'
          : defaultLabel;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity, finish: window.HalfpipedCartMutations?.begin() });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /** @param {string | undefined} value */
  #getNumericPrice(value) {
    if (!value) return null;

    const price = Number(value);
    return Number.isFinite(price) && price >= 0 ? price : null;
  }

  #getCurrencyCode() {
    return (
      window.Shopify?.currency?.active ||
      document.querySelector('[data-currency-code]')?.getAttribute('data-currency-code') ||
      'SGD'
    );
  }

  /** @param {string | undefined | null} value */
  #getPriceFromText(value) {
    if (!value) return null;

    const price = convertMoneyToMinorUnits(value, this.#getCurrencyCode());
    return Number.isFinite(price) && price >= 0 ? price : null;
  }

  /** @param {Element | Document} productInfo */
  #getSelectedVariantPrice(productInfo) {
    const dataPrice = this.#getNumericPrice(this.dataset.variantPrice);
    if (dataPrice !== null) return dataPrice;

    const pricePerItem =
      this.refs.pricePerItem?.dataset.variantPrice ||
      productInfo.querySelector('price-per-item')?.getAttribute('data-variant-price');
    const pricePerItemValue = this.#getPriceFromText(pricePerItem);
    if (pricePerItemValue !== null) return pricePerItemValue;

    const visiblePrice = productInfo.querySelector(
      'product-price .price__sale:not(.price__hidden) .price, product-price .price__regular:not(.price__hidden) .price, product-price [ref="priceContainer"] .price'
    )?.textContent;

    return this.#getPriceFromText(visiblePrice);
  }

  #getVariantMaxQuantity() {
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('quantity-selector-component input[ref="quantityInput"], quantity-selector-component input[name="quantity"]')
    );
    const quantityInputMax = quantityInput?.getAttribute('max');
    const maxValue = quantityInputMax === null || quantityInputMax === undefined || quantityInputMax === ''
      ? this.dataset.variantMaxQuantity
      : quantityInputMax;
    const maxQuantity = maxValue === null || maxValue === undefined || maxValue === '' ? Number.NaN : Number(maxValue);

    return Number.isFinite(maxQuantity) && maxQuantity >= 0 ? maxQuantity : null;
  }

  /** @param {Element | Document} productInfo */
  #getSelectedOptions(productInfo) {
    /** @type {Array<{name: string, value: string}>} */
    const selectedOptions = [];
    const seenOptions = new Set();
    const addOption = (name, value) => {
      const normalizedName = name?.trim() || 'Option';
      const normalizedValue = value?.replace(/\s+-\s+Unavailable$/i, '').trim();
      const key = `${normalizedName}:${normalizedValue}`;

      if (!normalizedValue || normalizedValue === 'Default Title' || seenOptions.has(key)) return;
      seenOptions.add(key);
      selectedOptions.push({ name: normalizedName, value: normalizedValue });
    };

    productInfo.querySelectorAll('variant-picker .variant-option--dropdowns').forEach((optionGroup) => {
      const label = optionGroup.querySelector('label')?.textContent;
      const select = /** @type {HTMLSelectElement | null} */ (optionGroup.querySelector('select'));
      addOption(label || select?.name || 'Option', select?.options[select.selectedIndex]?.textContent || '');
    });

    productInfo.querySelectorAll('variant-picker fieldset.variant-option').forEach((fieldset) => {
      const legend = fieldset.querySelector('legend')?.childNodes?.[0]?.textContent;
      const checkedInput = /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input[type="radio"]:checked'));
      addOption(legend || checkedInput?.name || 'Option', checkedInput?.value || '');
    });

    productInfo.querySelectorAll('[data-testid="variant-option-single"]').forEach((optionGroup) => {
      const clonedOption = optionGroup.cloneNode(true);
      if (!(clonedOption instanceof HTMLElement)) return;
      clonedOption.querySelector('[data-testid="variant-option-single-value"]')?.remove();
      const name = clonedOption.textContent;
      const value = optionGroup.querySelector('[data-testid="variant-option-single-value"]')?.textContent;
      addOption(name || 'Option', value || '');
    });

    this.querySelectorAll('select').forEach((select) => {
      addOption(
        select.getAttribute('aria-label') || select.name || 'Option',
        select.options[select.selectedIndex]?.textContent || ''
      );
    });

    return selectedOptions;
  }

  /**
   * @param {NodeListOf<AddToCartComponent>} addToCartContainers
   */
  #resetAddToCartVisual(addToCartContainers) {
    if (this.closest('.quick-add-modal')) return;

    addToCartContainers.forEach((container) => {
      container.resetAddToCartAnimation?.(PRODUCT_ADD_TO_CART_RESET_DELAY);
    });

    document.querySelectorAll('sticky-add-to-cart').forEach((stickyAddToCart) => {
      if (typeof stickyAddToCart.resetAddToCartAnimation === 'function') {
        stickyAddToCart.resetAddToCartAnimation(PRODUCT_ADD_TO_CART_RESET_DELAY);
      }
    });
  }

  /**
   * @param {HTMLFormElement} form
   * @param {FormData} formData
   * @param {number} quantity
   */
  #getPendingCartItem(form, formData, quantity) {
    const addonItem = this.closest('[data-hp-product-addon-item]');
    const productInfo =
      addonItem ||
      this.closest('.quick-add-modal') ||
      this.closest('.product-information') ||
      document;
    const title =
      this.dataset.productTitle ||
      addonItem?.querySelector('.hp-product-addon__title-link')?.getAttribute('aria-label') ||
      productInfo.querySelector('h1, h2, h3')?.textContent?.trim() ||
      document.title;
    const productImage = addonItem
      ? addonItem.querySelector('.hp-product-addon__image')
      : productInfo.querySelector('.product-media__image');
    const image =
      this.refs.addToCartButtonContainer?.dataset.productVariantMedia ||
      productImage?.currentSrc ||
      productImage?.src ||
      '';
    const productUrl =
      this.dataset.productUrl ||
      addonItem?.querySelector('.hp-product-addon__title-link')?.getAttribute('href') ||
      window.location.pathname + window.location.search;
    const variantId = String(formData.get('id') || '');
    const numericVariantId = Number(variantId);
    const normalizedVariantId = Number.isFinite(numericVariantId) ? numericVariantId : variantId;
    const unitPrice = this.#getSelectedVariantPrice(productInfo);
    const linePrice = unitPrice === null ? 0 : unitPrice * quantity;
    const variantMaxQuantity = this.#getVariantMaxQuantity();
    const selectedOptions = this.#getSelectedOptions(productInfo);
    const variantTitle = selectedOptions.map((option) => option.value).join(' / ');

    return {
      key: `pending-${variantId || this.id}`,
      id: normalizedVariantId,
      pending: true,
      variant_id: normalizedVariantId,
      variantId,
      properties: Object.fromEntries([...formData.entries()].filter(([key]) => /^properties\[.+\]$/.test(key))
        .map(([key, value]) => [key.slice(11, -1), typeof value === 'string' ? value : `Pending upload: ${value.name || ''}`])),
      selling_plan_id: formData.get('selling_plan') || null,
      product_id: this.dataset.productId || '',
      quantity,
      price: unitPrice || 0,
      final_line_price: linePrice,
      optimistic: true,
      image,
      product_title: title,
      title,
      url: productUrl,
      variant_title: variantTitle,
      options_with_values: selectedOptions,
      quantity_rule_max: variantMaxQuantity,
      quantityRuleMax: variantMaxQuantity,
    };
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    const itemCount = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault);
    const variantId = String(formData.get('id') || '');
    const pendingItem = this.#getPendingCartItem(form, formData, itemCount);
    const variantMaxQuantity = this.#getVariantMaxQuantity();
    const currentCartQuantity = Math.max(0, Number(this.dataset.cartQuantity) || 0);

    if (!overrideVariantId && variantMaxQuantity !== null && currentCartQuantity + itemCount > variantMaxQuantity) {
      this.syncStandaloneAvailability();
      return;
    }

    if (!overrideVariantId && this.closest('[data-hp-product-addon-item]')) {
      this.dataset.cartQuantity = String(currentCartQuantity + itemCount);
      this.syncStandaloneAvailability();
    }

    const finishCartMutation = window.HalfpipedCartMutations?.begin();
    this.dispatchEvent(
      new CustomEvent(ThemeEvents.cartAddPending, {
        bubbles: true,
        detail: {
          sourceId: this.id,
          data: {
            source: 'product-form-component',
            itemCount,
            productId: this.dataset.productId,
            variantId,
            pendingItem,
            cardImage: pendingItem.image,
            variantMaxQuantity,
          },
        },
      })
    );
    this.#resetAddToCartVisual(allAddToCartContainers);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    return fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          const errorMessage = String(response.description || response.message || 'Error adding to cart');
          finishCartMutation?.(new Error(errorMessage));

          const errorEvent = new CartErrorEvent(form.getAttribute('id') || '', errorMessage, response.description, response.errors);
          Object.assign(errorEvent.detail.data, { source: 'product-form-component', productId: this.dataset.productId,
            itemCount, variantId, pendingItem, variantMaxQuantity });
          this.dispatchEvent(errorEvent);

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            // Reuse the text node if the user is spam-clicking.
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) textNode.textContent = errorMessage;
            else addToCartTextError.appendChild(document.createTextNode(errorMessage));
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(errorMessage);

          this.#timeout = setTimeout(() => {
            addToCartTextError?.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              variantId,
              variantMaxQuantity,
            })
          );

          await this.#fetchAndUpdateCartQuantity();

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent(undefined, id.toString(), {
              source: 'product-form-component',
              itemCount,
              productId: this.dataset.productId,
              variantId,
              addedItem: response,
              pendingDrawerIntent: true,
              cardImage: pendingItem.image,
              variantMaxQuantity,
              sections: response.sections,
            })
          );

          // Keep the local quantity selector accurate without delaying the cart drawer.
          await this.#fetchAndUpdateCartQuantity();
        }
      })
      .catch((error) => {
        finishCartMutation?.(error?.cartWriteNotAttempted ? undefined : error);
        if (error?.cartWriteNotAttempted) {
          this.#reportBlockedCartAdd(error, { itemCount, variantId, pendingItem, variantMaxQuantity });
        } else console.error(error);
        this.#fetchAndUpdateCartQuantity();
      })
      .finally(() => {
        finishCartMutation?.();
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number, finish?: (error?: Error) => void}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(','),
    };

    const finishCartMutation = window.HalfpipedCartMutations?.begin();
    const finishBatch = error => {
      const mutationError = error?.cartWriteNotAttempted ? undefined : error;
      finishCartMutation?.(mutationError);
      items.forEach(item => item.finish?.(mutationError));
    };
    return fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          const errorMessage = String(response.description || response.message || 'Error adding to cart');
          finishBatch(new Error(errorMessage));

          const errorEvent = new CartErrorEvent(this.id, errorMessage, response.description, response.errors);
          Object.assign(errorEvent.detail.data, { source: 'product-form-component', productId: this.dataset.productId,
            itemCount: items.reduce((sum, item) => sum + item.quantity, 0), pendingDrawerIntent: false,
            variantId: items.length === 1 ? items[0]?.variantId : undefined, variantMaxQuantity: this.#getVariantMaxQuantity() });
          this.dispatchEvent(errorEvent);

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = errorMessage;
            } else {
              addToCartTextError.appendChild(document.createTextNode(errorMessage));
            }
            this.#setLiveRegionText(errorMessage);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
              variantId: items.length === 1 ? items[0]?.variantId : undefined,
              variantMaxQuantity: this.#getVariantMaxQuantity(),
            })
          );
          await this.#fetchAndUpdateCartQuantity();
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        this.dispatchEvent(
          new CartAddEvent(undefined, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            pendingDrawerIntent: false,
            addedItems: response.items,
            productId: this.dataset.productId,
            variantId: items.length === 1 ? items[0]?.variantId : undefined,
            variantMaxQuantity: this.#getVariantMaxQuantity(),
            sections: response.sections,
          })
        );

        // Keep the local quantity selector accurate without delaying the cart drawer.
        await this.#fetchAndUpdateCartQuantity();
      })
      .catch((error) => {
        finishBatch(error);
        if (error?.cartWriteNotAttempted) {
          this.#reportBlockedCartAdd(error, {
            itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
            variantId: items.length === 1 ? items[0]?.variantId : undefined,
            pendingDrawerIntent: false,
          });
          this.#fetchAndUpdateCartQuantity();
        } else console.error(error);
      })
      .finally(() => finishBatch());
  }

  #reportBlockedCartAdd(error, data) {
    const message = error.message || 'Review your saved extra items before adding this item.';
    const details = { ...data, source: 'product-form-component', productId: this.dataset.productId, message, cartWriteNotAttempted: true };
    const event = new CartErrorEvent(this.id, message, message);
    Object.assign(event.detail.data, details);
    this.dispatchEvent(event);
    this.dispatchEvent(new CartAddEvent({}, this.id, { ...details, didError: true }));
    const errorElement = this.refs.addToCartTextError;
    if (errorElement) {
      errorElement.classList.remove('hidden');
      const textNode = errorElement.childNodes[2];
      if (textNode) textNode.textContent = message;
      else errorElement.appendChild(document.createTextNode(message));
    }
    this.#setLiveRegionText(message);
    this.#timeout = setTimeout(() => {
      errorElement?.classList.add('hidden');
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';
    const variantPrice = Number(event.detail.resource?.price);
    if (Number.isFinite(variantPrice) && variantPrice >= 0) {
      this.dataset.variantPrice = String(variantPrice);
    } else {
      delete this.dataset.variantPrice;
    }

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const variantPrice = Number(event.detail.resource.price);
      if (Number.isFinite(variantPrice) && variantPrice >= 0) {
        this.dataset.variantPrice = String(variantPrice);
      }

      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
